import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock, 
  Star,
  ChevronLeft,
  MessageCircle
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { getClinic } from '../services/api';
import { toast } from '../hooks/use-toast';

// Available time slots for appointments
const TIME_SLOTS = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00'
];

const ClinicDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { specialties, countries } = useData();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState(0);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    service: ''
  });

  // Fetch clinic data
  useEffect(() => {
    const fetchClinic = async () => {
      setLoading(true);
      try {
        const data = await getClinic(id);
        setClinic(data);
      } catch (error) {
        console.error('Error fetching clinic:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [id]);

  // Get current branch data
  const currentBranch = useMemo(() => {
    if (!clinic) return null;
    if (clinic.branches && clinic.branches.length > 0) {
      return clinic.branches[selectedBranch];
    }
    // Return main clinic data as single branch
    return {
      id: clinic.id,
      name: 'Ubicación Principal',
      address: clinic.address,
      city: clinic.city,
      country: clinic.country,
      phone: clinic.phone,
      coordinates: clinic.coordinates,
      schedule: clinic.schedule
    };
  }, [clinic, selectedBranch]);

  // Get all branch coordinates for map
  const allBranches = useMemo(() => {
    if (!clinic) return [];
    if (clinic.branches && clinic.branches.length > 0) {
      return clinic.branches;
    }
    return [{
      id: clinic.id,
      name: 'Ubicación Principal',
      address: clinic.address,
      city: clinic.city,
      phone: clinic.phone,
      coordinates: clinic.coordinates
    }];
  }, [clinic]);

  // Combine all images: main image first, then gallery
  const allImages = useMemo(() => {
    if (!clinic) return [];
    const images = [];
    
    // Add main image first if it exists
    if (clinic.image) {
      images.push(clinic.image);
    }
    
    // Add gallery images
    if (clinic.gallery && Array.isArray(clinic.gallery)) {
      images.push(...clinic.gallery);
    }
    
    return images;
  }, [clinic]);

  // Get related clinics (same city or country)
  const relatedClinics = useMemo(() => {
    // Esta funcionalidad se puede mejorar con un endpoint específico
    return [];
  }, [clinic]);

  const getSpecialtyName = (id) => {
    if (!specialties) return id;
    const specialty = specialties.find(s => s.id === id);
    return specialty ? specialty.name : id;
  };

  const getCountryInfo = (code) => {
    if (!countries) return null;
    return countries.find(c => c.code === code);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando clínica...</p>
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Clínica no encontrada</h1>
          <Button onClick={() => navigate('/clinicas')} className="bg-cyan-500 hover:bg-cyan-600">
            Volver al directorio
          </Button>
        </div>
      </div>
    );
  }

  const countryInfo = getCountryInfo(clinic.country);

  const handleAppointmentChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWhatsAppAppointment = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!appointmentData.name || !appointmentData.phone || !appointmentData.date || !appointmentData.time) {
      toast({
        title: "Campos requeridos",
        description: "Por favor completa todos los campos obligatorios",
        variant: "destructive"
      });
      return;
    }
    
    // Get the phone number (clean it for WhatsApp)
    const phoneNumber = currentBranch?.phone?.replace(/[^0-9]/g, '') || clinic.phone.replace(/[^0-9]/g, '');
    
    // Format date for display
    const dateFormatted = new Date(appointmentData.date + 'T00:00:00').toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Build the message
    const message = `¡Hola! Me gustaría agendar una cita en ${clinic.name}.

*Datos del paciente:*
- Nombre: ${appointmentData.name}
- Teléfono: ${appointmentData.phone}
- Fecha preferida: ${dateFormatted}
- Hora preferida: ${appointmentData.time}
- Servicio: ${appointmentData.service || 'Consulta general'}

¡Gracias!`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp Web
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
    
    // Close form and reset
    setShowAppointmentForm(false);
    setAppointmentData({ name: '', phone: '', date: '', time: '', service: '' });
    
    toast({
      title: "Redirigiendo a WhatsApp",
      description: "Completa el envío del mensaje en WhatsApp.",
    });
  };

  const handleCall = () => {
    const phoneNumber = currentBranch?.phone || clinic.phone;
    window.location.href = `tel:${phoneNumber}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/clinicas')}
            className="mb-4 hover:text-cyan-600"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Volver al directorio
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Image Gallery */}
            <div className="lg:col-span-2">
              <div className="relative overflow-hidden rounded-xl shadow-lg mb-4" style={{ height: '500px' }}>
                <img
                  src={allImages[selectedImage] || clinic.image}
                  alt={clinic.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {allImages && allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {allImages.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 ${
                        selectedImage === idx ? 'ring-4 ring-cyan-500' : 'opacity-70 hover:opacity-100'
                      }`}
                      style={{ height: '100px' }}
                    >
                      <img
                        src={img}
                        alt={`${clinic.name} - ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Clinic Info Card */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="shadow-xl">
                <CardContent className="p-6">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{clinic.name}</h1>
                  
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="h-5 w-5 mr-2 text-cyan-500" />
                    <span>{currentBranch.city}, {countryInfo?.flag} {countryInfo?.name}</span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {clinic.specialties.map((spec, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-cyan-100 text-cyan-700">
                        {getSpecialtyName(spec)}
                      </Badge>
                    ))}
                  </div>

                  <div className="space-y-3 mb-6">
                    {clinic.email && (
                      <a href={`mailto:${clinic.email}`} className="flex items-center text-gray-700 hover:text-cyan-600 transition-colors">
                        <Mail className="h-5 w-5 mr-3 text-cyan-500" />
                        <span className="text-sm">{clinic.email}</span>
                      </a>
                    )}
                    
                    {clinic.website && (
                      <a href={`https://${clinic.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-cyan-600 transition-colors">
                        <Globe className="h-5 w-5 mr-3 text-cyan-500" />
                        <span className="text-sm">{clinic.website}</span>
                      </a>
                    )}
                  </div>

                  <div className="space-y-3 mb-6">
                    <Button 
                      onClick={() => setShowAppointmentForm(true)}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Agendar cita por WhatsApp
                    </Button>
                    <Button 
                      onClick={handleCall}
                      variant="outline"
                      className="w-full border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      Llamar
                    </Button>
                  </div>

                  {/* Appointment Form Modal */}
                  {showAppointmentForm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <Card className="w-full max-w-md">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-900">Agendar Cita</h3>
                            <button 
                              onClick={() => setShowAppointmentForm(false)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              ✕
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Completa el formulario y te redirigiremos a WhatsApp para confirmar tu cita.
                          </p>
                          <form onSubmit={handleWhatsAppAppointment} className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nombre completo *
                              </label>
                              <Input
                                type="text"
                                name="name"
                                value={appointmentData.name}
                                onChange={handleAppointmentChange}
                                placeholder="Tu nombre"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Teléfono *
                              </label>
                              <Input
                                type="tel"
                                name="phone"
                                value={appointmentData.phone}
                                onChange={handleAppointmentChange}
                                placeholder="+591 71234567"
                                required
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Fecha preferida *
                                </label>
                                <Input
                                  type="date"
                                  name="date"
                                  value={appointmentData.date}
                                  onChange={handleAppointmentChange}
                                  min={new Date().toISOString().split('T')[0]}
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Hora preferida *
                                </label>
                                <Select
                                  value={appointmentData.time}
                                  onValueChange={(value) => setAppointmentData(prev => ({ ...prev, time: value }))}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar hora" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {TIME_SLOTS.map((slot) => (
                                      <SelectItem key={slot} value={slot}>
                                        {slot}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Servicio o motivo de consulta
                              </label>
                              <Input
                                type="text"
                                name="service"
                                value={appointmentData.service}
                                onChange={handleAppointmentChange}
                                placeholder="Ej: Limpieza dental, Ortodoncia..."
                              />
                            </div>
                            <div className="flex gap-3 pt-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAppointmentForm(false)}
                                className="flex-1"
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="submit"
                                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                              >
                                <MessageCircle className="h-4 w-4 mr-2" />
                                Enviar por WhatsApp
                              </Button>
                            </div>
                          </form>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Branch Selector - Only show if multiple branches */}
              {clinic.branches && clinic.branches.length > 1 && (
                <Card className="shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">Sucursales</h3>
                      <Badge variant="secondary" className="bg-cyan-100 text-cyan-700">
                        {clinic.branches.length} ubicaciones
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {clinic.branches.map((branch, idx) => (
                        <div
                          key={branch.id}
                          onClick={() => setSelectedBranch(idx)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                            selectedBranch === idx
                              ? 'border-cyan-500 bg-cyan-50 shadow-md'
                              : 'border-gray-200 hover:border-cyan-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-gray-900 text-sm">{branch.name}</h4>
                            {selectedBranch === idx && (
                              <Badge className="bg-cyan-500 text-xs">Seleccionada</Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-gray-600 flex items-start">
                              <MapPin className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0 text-cyan-500" />
                              <span>{branch.address}</span>
                            </p>
                            <p className="text-xs text-gray-600 flex items-center">
                              <Phone className="h-3 w-3 mr-1 text-cyan-500" />
                              {branch.phone}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Current Branch Info Card */}
              <Card className="shadow-xl sticky top-4">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <MapPin className="h-5 w-5 mr-2 text-cyan-500" />
                    <h3 className="font-semibold text-gray-900">
                      {clinic.branches && clinic.branches.length > 1 ? currentBranch.name : 'Ubicación'}
                    </h3>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start text-gray-700">
                      <MapPin className="h-4 w-4 mr-2 text-cyan-500 flex-shrink-0 mt-1" />
                      <span className="text-sm">{currentBranch.address}</span>
                    </div>
                    
                    <a href={`tel:${currentBranch.phone}`} className="flex items-center text-gray-700 hover:text-cyan-600 transition-colors">
                      <Phone className="h-4 w-4 mr-2 text-cyan-500" />
                      <span className="text-sm">{currentBranch.phone}</span>
                    </a>
                  </div>

                  {/* Schedule */}
                  {currentBranch.schedule && currentBranch.schedule.length > 0 && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center mb-3">
                        <Clock className="h-5 w-5 mr-2 text-cyan-500" />
                        <h3 className="font-semibold text-gray-900">Horarios</h3>
                      </div>
                      <div className="space-y-2 text-sm">
                        {currentBranch.schedule.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-gray-600">{item.day}:</span>
                            <span className="font-medium text-gray-900">{item.hours}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Sobre la clínica</h2>
                <p className="text-gray-700 leading-relaxed">
                  { clinic.description} <br></br>  <br></br>
                  {clinic.about_long  }
                </p>
              </CardContent>
            </Card>

            {/* Services Section */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Servicios y Especialidades</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {clinic.specialties.map((spec, idx) => {
                    const specialty = specialties.find(s => s.id === spec);
                    return (
                      <div key={idx} className="flex items-start space-x-3 p-4 bg-cyan-50 rounded-lg hover:bg-cyan-100 transition-colors">
                        <div className="flex-shrink-0 w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center">
                          <Star className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{specialty?.name || spec}</h3>
                          <p className="text-sm text-gray-600">{specialty?.description || ''}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Map Section */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {allBranches.length > 1 ? 'Ubicaciones' : 'Ubicación'}
                </h2>
                {allBranches.length > 1 && (
                  <p className="text-gray-600 mb-4">
                    Esta clínica cuenta con {allBranches.length} sucursales
                  </p>
                )}
                
                {/* List of branches if multiple */}
                {allBranches.length > 1 && (
                  <div className="mb-6 space-y-3">
                    {allBranches.map((branch, idx) => (
                      <div
                        key={branch.id}
                        onClick={() => setSelectedBranch(idx)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedBranch === idx
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-gray-200 hover:border-cyan-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{branch.name}</h4>
                            <p className="text-sm text-gray-600 flex items-start">
                              <MapPin className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0 text-cyan-500" />
                              {branch.address}
                            </p>
                            <p className="text-sm text-gray-600 mt-1 flex items-center">
                              <Phone className="h-4 w-4 mr-1 text-cyan-500" />
                              {branch.phone}
                            </p>
                          </div>
                          {selectedBranch === idx && (
                            <Badge className="bg-cyan-500">Seleccionada</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="rounded-lg overflow-hidden shadow-md" style={{ height: '400px' }}>
                  <MapContainer
                    center={[currentBranch.coordinates.lat, currentBranch.coordinates.lng]}
                    zoom={allBranches.length > 1 ? 12 : 15}
                    style={{ height: '100%', width: '100%' }}
                    key={`map-${selectedBranch}`}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {allBranches.map((branch, idx) => (
                      <Marker 
                        key={branch.id} 
                        position={[branch.coordinates.lat, branch.coordinates.lng]}
                      >
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-bold text-gray-900 mb-1">
                              {clinic.name}
                            </h3>
                            <p className="font-semibold text-cyan-600 text-sm mb-1">{branch.name}</p>
                            <p className="text-sm text-gray-600 mb-1">{branch.address}</p>
                            <p className="text-sm text-gray-600">{branch.phone}</p>
                            {selectedBranch !== idx && (
                              <button
                                onClick={() => setSelectedBranch(idx)}
                                className="mt-2 text-xs text-cyan-600 hover:text-cyan-700 font-semibold"
                              >
                                Seleccionar esta sucursal
                              </button>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Related Clinics */}
            {relatedClinics.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Clínicas relacionadas</h3>
                  <div className="space-y-4">
                    {relatedClinics.map((relatedClinic) => (
                      <Link
                        key={relatedClinic.id}
                        to={`/clinicas/${relatedClinic.id}`}
                        className="block group"
                      >
                        <div className="flex space-x-3">
                          <div className="flex-shrink-0 w-20 h-20 overflow-hidden rounded-lg">
                            <img
                              src={relatedClinic.image}
                              alt={relatedClinic.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
                              {relatedClinic.name}
                            </h4>
                            <p className="text-sm text-gray-600">{relatedClinic.city}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicDetail;
